/**
 * Label keys, derived from the caption.
 *
 * Left to themselves authors write `tab1`, `table_results_final`, or nothing —
 * and *nothing* is why referencing was cumbersome: a table with no label cannot
 * be pointed at, so the `@` picker had nothing to offer. Deriving the key from
 * the caption makes every captioned block referenceable the moment it is saved.
 * (Olivia, SPEC-references §4.)
 *
 * Two rules matter more than the slug:
 *
 * - **One-way.** A key is derived only when the block has none. Rename the
 *   caption later and the key stays, because something may already point at it.
 * - **A collision warns; it never blocks.** LaTeX silently keeping the last of
 *   two identical labels is the bug. Refusing to save would be a worse one.
 */

/** Conventional prefix per block kind — readable in a list with no type column. */
const PREFIX: Record<string, string> = {
  table: "tab",
  figure: "fig",
  equation: "eq",
  heading: "sec",
  section: "sec",
  algorithm: "alg",
  code: "lst",
  listing: "lst",
  theorem: "thm",
};

/** The prefix for a block kind, or `ref` for anything without a convention. */
export function labelPrefix(kind: string): string {
  return PREFIX[kind] ?? "ref";
}

/** The kind word for a message — "a table", "an equation". */
export function kindWord(kind: string): string {
  const word = PREFIX[kind] ? (kind === "heading" ? "section" : kind === "code" ? "listing" : kind) : "block";
  return /^[aeiou]/.test(word) ? `an ${word}` : `a ${word}`;
}

const MAX_SLUG = 40;

/**
 * A caption, reduced to what a label key may contain.
 *
 * LaTeX in a caption is read for its words, not its commands: `\textbf{Top-1}`
 * contributes "top-1", and inline maths is dropped rather than spelled out,
 * because `$\alpha$-scaling` should not become `alpha-scaling` on one block
 * and `scaling` on the next depending on how it was typed.
 */
export function slugify(caption: string): string {
  const words = caption
    .replace(/\$[^$]*\$/g, " ")                    // inline maths
    .replace(/\\[a-zA-Z]+\*?(\[[^\]]*\])?\{([^}]*)\}/g, " $2 ") // \cmd{text} → text
    .replace(/\\[a-zA-Z]+\*?/g, " ")               // bare commands
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")               // é → e
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (words.length <= MAX_SLUG) return words;
  // Cut at a word boundary, so a key never ends mid-word.
  const cut = words.slice(0, MAX_SLUG);
  const lastDash = cut.lastIndexOf("-");
  return (lastDash > 10 ? cut.slice(0, lastDash) : cut).replace(/-+$/, "");
}

/** The first of `key`, `key-2`, `key-3`… not already taken. */
export function nextFree(key: string, taken: ReadonlySet<string>): string {
  if (!taken.has(key)) return key;
  for (let n = 2; ; n++) {
    const candidate = `${key}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/**
 * The key a block should get from its caption, or null when there is nothing
 * to derive from — an empty caption, or one that is only maths.
 *
 * A derived key never collides: the system chose it, and warning the author
 * about a clash we created would be absurd. So it takes the next free suffix.
 */
export function deriveLabel(caption: string, kind: string, taken: ReadonlySet<string>): string | null {
  const slug = slugify(caption ?? "");
  if (!slug) return null;
  return nextFree(`${labelPrefix(kind)}:${slug}`, taken);
}

/** Another block that already uses a key. */
export interface LabelOwner {
  key: string;
  kind: string;
}

/**
 * Whether a key the *author* typed clashes with another block's. Their own
 * block is excluded by the caller — a block does not collide with itself.
 */
export function collision(key: string, others: readonly LabelOwner[]): LabelOwner | null {
  const k = key.trim();
  if (!k) return null;
  return others.find((o) => o.key === k) ?? null;
}
